import CellInput from '@Components/ViewWork/Table/ValidationCells/CellInput'
import { canAmend } from '@App/helpers/permission'
import Button from '@Components/Buttons/Button'
import CellDisplayList from '@Components/ViewWork/Table/ValidationCells/CellDisplayList'
import React, { JSX, useContext } from 'react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import usePublisherService from '@App/services/usePublisherService'
import { PublisherModel } from '@Entities/work/publisher-model'
import { OriginalPublisherModel } from '@Entities/work/original-publisher-model'

interface GeneratePublisherTableCellsProps {
    publisher: OriginalPublisherModel
    index: number
    getKeyPrefix(publisher: OriginalPublisherModel | PublisherModel): string
}

interface GenerateAdminTableCellsProps extends GeneratePublisherTableCellsProps {
    admin: PublisherModel
}

export const generatePublisherTableCells = ({
    publisher,
    index,
    getKeyPrefix,
}: GeneratePublisherTableCellsProps): JSX.Element[] => {
    const { publisherStepStore } = useContext<RootStore>(StoresContext)
    const publisherService = usePublisherService()
    return [
        <CellInput
            defaultValue={publisher.publisherName}
            readOnly
            name={`rows.publisherRows[${index}].publisherName`}
            key={`${getKeyPrefix(publisher)}.publisherName`}
            type="text"
        />,
        <CellInput
            name={`rows.publisherRows[${index}].publisherIPI`}
            key={`${getKeyPrefix(publisher)}.publisherIPI`}
            defaultValue={publisher.ipiNumber ?? ''}
            readOnly
            type="text"
        />,
        <CellInput
            name={`rows.publisherRows[${index}].publisherIPINumber`}
            key={`${getKeyPrefix(publisher)}.publisherIPINumber`}
            defaultValue={publisher.hfaPublisherNumber ?? ''}
            readOnly
            type="text"
        />,
        <CellInput
            name={`rows.publisherRows[${index}].publisherShare`}
            key={`${getKeyPrefix(publisher)}.publisherShare`}
            defaultValue={publisher.publisherShare}
            min={0}
            max={100}
            type="number"
            readOnly={!canAmend(publisher.amend) || publisher.administratorPublishers.length > 0}
            onValueChanged={(value) => {
                publisherService.updatePublisherShare(publisher, value as number)
            }}
        />,
        <Button
            className="add-administrator-button"
            key={`${getKeyPrefix(publisher)}-add-admin-button`}
            type="primary"
            mainColor
            onClick={() => {
                publisherStepStore.initAddAP(publisher)
            }}
            disabled={!canAmend(publisher.amend)}
        >
            Add
        </Button>,
        <CellDisplayList
            readOnly={!canAmend(publisher.amend)}
            key={`${getKeyPrefix(publisher)}-display-list`}
            items={publisher.writers}
            onAddToListClicked={() => {
                publisherStepStore.initAddWriterToOriginalPublisher(publisher)
            }}
            onRemoveFromListClicked={(writerModel) => {
                publisherService.removeLinkedWriter(publisher, writerModel)
            }}
        />,
    ]
}

export const generateAdminTableCells = ({
    admin,
    publisher,
    index,
    getKeyPrefix,
}: GenerateAdminTableCellsProps): JSX.Element[] => {
    const publisherService = usePublisherService()
    return [
        <CellInput
            defaultValue={admin.publisherName}
            readOnly
            name={`rows.administratorRows[${index}].administrator`}
            key={`${getKeyPrefix(admin)}.administrator`}
            type="text"
        />,
        <CellInput
            name={`rows.administratorRows[${index}].administratorIPI`}
            key={`${getKeyPrefix(admin)}.administratorIPI`}
            defaultValue={admin.ipiNumber}
            readOnly
            type="text"
        />,
        <CellInput
            name={`rows.administratorRows[${index}].administratorIPINumber`}
            key={`${getKeyPrefix(admin)}.administratorIPINumber`}
            defaultValue={admin.hfaPublisherNumber}
            readOnly
            type="text"
        />,
        <CellInput
            name={`rows.administratorRows[${index}].administratorShare`}
            key={`${getKeyPrefix(admin)}.administratorShare`}
            defaultValue={admin.publisherShare}
            min={0.0}
            max={100.06}
            type="number"
            readOnly={!canAmend(admin.amend)}
            onValueChanged={(value) => {
                publisherService.updateAdministratorPublisherShare(publisher, admin, value as number)
            }}
        />,
    ]
}
